// Role-based configuration for the shared application list page
// (Common/ApplicationList.vue). One entry per list type, then per role.
//
// request(ctx) returns { url, data } for the list call. ctx holds:
//   page, fromDate, toDate, search, appStatus, filters, districtid, campId, camp
// detail(id) returns the router location of the role's detail page.
import CryptoJS from "crypto-js";
import { detailLocation as cwwDetailLocation } from "./cwwConfig";

const SECRET = "encryptionSecret";
const enc = (id) => CryptoJS.AES.encrypt(id, SECRET).toString();
const encrypted = (path) => (id) => ({ path, query: { id: enc(id) } });
const plain = (path) => (id) => ({ path, query: { id } });

// Common request fields; filter keys are only sent when set.
function base(ctx, { limit = 10, division = false } = {}) {
  const data = {
    page: ctx.page,
    limit,
    fromDate: ctx.fromDate || undefined,
    toDate: ctx.toDate || undefined,
    search: ctx.search || undefined,
  };
  if (division && ctx.filters.divisionid) data.divisionid = ctx.filters.divisionid;
  if (ctx.filters.rangeid) data.rangeid = ctx.filters.rangeid;
  if (ctx.filters.campType && ctx.filters.campType !== "all") data.campType = ctx.filters.campType;
  if (ctx.paymentSource && ctx.paymentSource !== "all") data.paymentSource = ctx.paymentSource;
  return data;
}

// ---------- Row actions ----------
const VIEW = { type: "view" };
const TIMELINE = { type: "timeline" };
// const FORWARD = { type: "forward" };
const SANCTION = {
  type: "download",
  label: "Sanction order",
  icon: "mdi-file-download-outline",
  url: "/sanction/pdf",
  auth: "token",
  fileName: "Sanction Order.pdf",
};
const sanctionWhen = (when) => ({ ...SANCTION, when });
// Shows the sanction order download once the super admin has sanctioned it.
const SANCTIONED = sanctionWhen((item) => item.superAdminApprovalStatus === "Success");
// Super admin removes an application completely (with its payment records).
// Lists that have this action also get the bulk "Select" mode (ApplicationList.vue).
export const DELETE_APP = { type: "deleteApp", label: "Delete", icon: "mdi-delete-outline" };
// Super admin records an offline payment; only for sanctioned, unpaid paid camps.
const MARK_PAID = {
  type: "markPaid",
  label: "Mark as paid",
  icon: "mdi-cash-check",
  when: (item) =>
    item.superAdminApprovalStatus === "Success" &&
    item.paymentStatus !== "Success" &&
    item.status === "Active" &&
    (item.campid ? item.campid.campType !== "free" : Number(item.amount) > 0),
};
const REJECTION = {
  type: "download",
  label: "Rejection order",
  icon: "mdi-file-cancel-outline",
  url: "/rejection/order",
  auth: "bearer",
  fileName: "Rejection Order.pdf",
  when: (item) => item.divisonApprovalStatus === "rejected" || item.superAdminApprovalStatus === "rejected",
};
const cancellationOrder = (label, when) => ({
  type: "download",
  label,
  icon: "mdi-file-download-outline",
  url: "/cancelby/applicant/pdf",
  auth: "bearer",
  fileName: `${label}.pdf`,
  when,
});

// ---------- Forward ("send to") setups for pending lists ----------
// Shared with the detail pages; see ForwardDialog.vue for the fields.
export const FORWARD_SETUPS = {
  clerk: {
    listUrl: "/clerk/forward/list",
    targets: [{ key: "superintendent", label: "DFO MH", role: "Superintendent" }],
    amountFields: "strict",
  },
  superint: {
    listUrl: "/superintendent/forward/list",
    targets: [
      { key: "clerk", label: "DFO Clerk", role: "Clerk", revert: true },
      { key: "dfo", label: "DFO", role: "dfo" },
    ],
    amountFields: "strict",
  },
  adminclerk: {
    cwwRole: "adminclerk",
    includeApplicant: true,
    preselectFirst: true,
    amountFields: "optional",
  },
  adminsuperint: {
    cwwRole: "adminsuperint",
    includeApplicant: true,
    preselectFirst: true,
    amountFields: "optional",
  },
  dcf: {
    cwwRole: "dcf",
    amountFields: "optional",
  },
};

// ---------- List types ----------
const LISTS = {
  pending: {
    title: "Pending Applications",
    icon: "mdi-clock-outline",
    status: "pending",
    emptyText: "No pending applications",
    roles: {
      admin: {
        subtitle: "Applications awaiting head-office review. Narrow down by district and camp.",
        scope: "districtCamp",
        request: (ctx) =>
          ctx.campId
            ? {
                url: "/camp/application",
                data: { campid: ctx.campId, page: ctx.page, fromDate: ctx.fromDate || undefined, search: ctx.search || undefined, limit: 10 },
              }
            : { url: "/district/camps", data: { ...base(ctx), district: ctx.districtid || undefined } },
        detail: plain("/CampDetailedViewAdmin"),
        actions: [VIEW, TIMELINE],
      },
      division: {
        subtitle: "Applications waiting at your desk. Filter by camp, range or camp type.",
        // Same list as the other desks (everything at the DFO seat), plus a camp filter.
        campFilter: true,
        advancedFilters: true,
        request: (ctx) => ({ url: "/forwarded/apps", data: { ...base(ctx), campid: ctx.campId || undefined } }),
        detail: encrypted("/CampDetailedViewDivision"),
        actions: [VIEW, TIMELINE],
      },
      clerk: {
        subtitle: "Applications forwarded to you for verification.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/forwarded/apps", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewClerk"),
        forward: FORWARD_SETUPS.clerk,
        actions: [VIEW, TIMELINE],
      },
      superint: {
        subtitle: "Applications forwarded to you for review.",
        request: (ctx) => ({ url: "/forwarded/apps", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewSuperint"),
        forward: FORWARD_SETUPS.superint,
        actions: [VIEW, TIMELINE],
      },
      adminclerk: {
        subtitle: "Applications forwarded to you for verification.",
        request: (ctx) => ({ url: "/forwarded/apps", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("adminclerk", id),
        forward: FORWARD_SETUPS.adminclerk,
        actions: [VIEW, TIMELINE],
      },
      adminsuperint: {
        subtitle: "Applications forwarded to you for review.",
        request: (ctx) => ({ url: "/forwarded/apps", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("adminsuperint", id),
        forward: FORWARD_SETUPS.adminsuperint,
        actions: [VIEW, TIMELINE],
      },
      dcf: {
        subtitle: "Applications forwarded by the ministerial head for your review.",
        request: (ctx) => ({ url: "/forwarded/apps", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("dcf", id),
        forward: FORWARD_SETUPS.dcf,
        actions: [VIEW, TIMELINE],
      },
    },
  },

  // Super admin only: applications paid through Namudekeralam (see /all/applications paymentSource).
  namudekeralam: {
    title: "Namudekeralam Applications",
    icon: "mdi-tree-outline",
    status: "auto",
    emptyText: "No applications from Namudekeralam",
    roles: {
      admin: {
        subtitle: "Applications submitted through Namudekeralam.",
        advancedFilters: true,
        statusFilter: true,
        request: (ctx) => ({
          url: "/all/applications",
          data: {
            ...base(ctx, { division: true }),
            status: "yes",
            appStatus: ctx.appStatus || "all",
            paymentSource: "namudekeralam",
          },
        }),
        detail: encrypted("/CampDetailedAdmin"),
        actions: [VIEW, TIMELINE, SANCTIONED, MARK_PAID, DELETE_APP],
      },
    },
  },

  all: {
    title: "All Applications",
    icon: "mdi-file-document-multiple-outline",
    status: "auto",
    emptyText: "No applications found",
    roles: {
      admin: {
        subtitle: "Every nature camp application across divisions.",
        advancedFilters: true,
        statusFilter: true,
        paymentSourceFilter: true,
        request: (ctx) => ({
          url: "/all/applications",
          data: { ...base(ctx, { division: true }), status: "yes", appStatus: ctx.appStatus || "all" },
        }),
        detail: encrypted("/CampDetailedAdmin"),
        actions: [VIEW, TIMELINE, SANCTIONED, MARK_PAID, DELETE_APP],
      },
      division: {
        subtitle: "All applications for camps in your division.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/my/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewDivision"),
        actions: [
          VIEW,
          TIMELINE,
          sanctionWhen((item) => item.divisonApprovalStatus === "Success" && item.superAdminApprovalStatus === "Success"),
          REJECTION,
        ],
      },
      clerk: {
        subtitle: "All applications in your division.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/clerk/all/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewClerk"),
        actions: [VIEW, TIMELINE, SANCTIONED],
      },
      superint: {
        subtitle: "All applications in your division.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/superint/all/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewSuperint"),
        actions: [VIEW, TIMELINE, SANCTIONED],
      },
      adminclerk: {
        subtitle: "All applications handled by the head office.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/adminclerk/all/applications", data: base(ctx, { division: true }) }),
        detail: encrypted("/Adminclerkview"),
        actions: [VIEW, TIMELINE, SANCTIONED],
      },
      adminsuperint: {
        subtitle: "All applications handled by the head office.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/adminclerk/all/applications", data: base(ctx, { division: true }) }),
        detail: (id) => cwwDetailLocation("adminsuperint", id),
        actions: [VIEW, TIMELINE, SANCTIONED],
      },
      dcf: {
        subtitle: "All applications across every camp.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/adminclerk/all/applications", data: base(ctx, { division: true }) }),
        detail: (id) => cwwDetailLocation("dcf", id),
        actions: [VIEW, TIMELINE, SANCTIONED],
      },
    },
  },

  approved: {
    title: "Approved Applications",
    icon: "mdi-check-decagram-outline",
    status: "approved",
    emptyText: "No approved applications",
    roles: {
      admin: {
        subtitle: "Applications sanctioned by the head office.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx, { division: true }) }),
        detail: encrypted("/CampDetailedDivision"),
        actions: [VIEW, TIMELINE, SANCTION, MARK_PAID, DELETE_APP],
      },
      division: {
        subtitle: "Sanctioned applications for your division's camps.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedDivision"),
        actions: [VIEW, TIMELINE, { ...SANCTION, label: "Approval order" }],
      },
      clerk: {
        subtitle: "Sanctioned applications in your division.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewClerk"),
        actions: [VIEW, TIMELINE, SANCTION],
      },
      superint: {
        subtitle: "Sanctioned applications in your division.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: encrypted("/CampDetailedViewSuperint"),
        actions: [VIEW, TIMELINE, SANCTION],
      },
      adminclerk: {
        subtitle: "Sanctioned applications handled by the head office.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("adminclerk", id),
        actions: [VIEW, TIMELINE, SANCTION],
      },
      adminsuperint: {
        subtitle: "Sanctioned applications handled by the head office.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("adminsuperint", id),
        actions: [VIEW, TIMELINE, SANCTION],
      },
      dcf: {
        subtitle: "Sanctioned applications across all camps.",
        advancedFilters: true,
        request: (ctx) => ({ url: "/admin/approved/applications", data: base(ctx) }),
        detail: (id) => cwwDetailLocation("dcf", id),
        actions: [VIEW, TIMELINE, SANCTION],
      },
    },
  },

  cancelRequests: {
    title: "Cancel Requests",
    icon: "mdi-cancel",
    status: "cancelRequest",
    emptyText: "No cancellation requests",
    roles: {
      division: {
        subtitle: "Cancellation requests raised by applicants for your camps.",
        noSearch: true,
        request: (ctx) => ({
          url: "/division/cancel/requests",
          data: { page: ctx.page, fromDate: ctx.fromDate || undefined, toDate: ctx.toDate || undefined, limit: 10 },
        }),
        detail: encrypted("/DivisionCancelView"),
        actions: [{ ...VIEW, label: "Review" }, TIMELINE],
      },
      clerk: {
        subtitle: "Cancellation requests raised by applicants in your division.",
        noSearch: true,
        request: (ctx) => ({
          url: "/division/cancel/requests",
          data: { page: ctx.page, fromDate: ctx.fromDate || undefined, toDate: ctx.toDate || undefined, limit: 10 },
        }),
        detail: plain("/CampDetailedView"),
        actions: [VIEW, TIMELINE],
      },
      // List only: the DFO decides cancel requests.
      dcf: {
        subtitle: "Cancellation requests raised by applicants across all camps.",
        noSearch: true,
        request: (ctx) => ({
          url: "/division/cancel/requests",
          data: { page: ctx.page, fromDate: ctx.fromDate || undefined, toDate: ctx.toDate || undefined, limit: 10 },
        }),
        detail: plain("/CampDetailedView"),
        actions: [VIEW, TIMELINE],
      },
    },
  },

  cancelled: {
    title: "Cancelled Applications",
    icon: "mdi-close-circle-outline",
    status: "cancelled",
    emptyText: "No cancelled applications",
    roles: (() => {
      const proceedings = cancellationOrder(
        "Proceedings order",
        (item) => item.editStatus === "Disable" && item.status === "Cancelled"
      );
      const common = (subtitle, extra) => ({
        subtitle,
        advancedFilters: true,
        request: (ctx) => ({ url: "/all/cancelled/applications", data: { ...base(ctx, extra), status: "yes" } }),
        detail: plain("/CampDetailedView"),
        actions: [VIEW, TIMELINE, proceedings],
      });
      return {
        admin: {
          ...common("Applications cancelled by applicants or rejected.", { division: true }),
          actions: [VIEW, TIMELINE, cancellationOrder("Cancellation order")],
        },
        division: common("Cancelled applications for your division's camps."),
        clerk: common("Cancelled applications in your division."),
        superint: common("Cancelled applications in your division."),
        adminclerk: { ...common("Cancelled applications handled by the head office.", { division: true }), detail: (id) => cwwDetailLocation("adminclerk", id) },
        adminsuperint: { ...common("Cancelled applications handled by the head office.", { division: true }), detail: (id) => cwwDetailLocation("adminsuperint", id) },
        dcf: common("Cancelled applications across all camps.", { division: true }),
      };
    })(),
  },
};

export function getListConfig(listType, role) {
  const list = LISTS[listType];
  if (!list || !list.roles[role]) return null;
  return { ...list, ...list.roles[role], roles: undefined };
}
